/**
 * This file contains authentication parameters. Contents of this file
 * is roughly the same across other MSAL.js libraries. These parameters
 * are used to initialize Angular and MSAL Angular configurations in
 * in app.module.ts file.
 */

import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  LogLevel,
  Configuration,
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
  BrowserAuthOptions,
  CacheOptions,
  BrowserSystemOptions,
} from '@azure/msal-browser';
import { isDevMode } from '@angular/core';
import { EnvironmentConfigurationService } from '@nimbus-library/features/configuration';
import { AssistantEnvironmentConfiguration } from './interfaces/assistant-environment.configuration';
import { LoggingService } from './services/logging.service';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

interface assistantMsalConfig {
  auth?: BrowserAuthOptions;
  cache: CacheOptions;
  system: BrowserSystemOptions;
}

export const AuthScopes = {
  oidc: ['openid', 'profile'],
  user: ['User.Read'],
  users: ['User.ReadBasic.All'],
  teams: ['https://auth.msft.communication.azure.com/Teams.ManageCalls', 'https://auth.msft.communication.azure.com/Teams.ManageChats'],
  presence: ['Presence.Read'],
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  graphApiMe: {
    endpoint: 'https://graph.microsoft.com/v1.0/me',
    scopes: AuthScopes.user,
  },
  graphApiMyAvatar: {
    endpoint: 'https://graph.microsoft.com/v1.0/me/photos/48x48/$value',
    scopes: AuthScopes.user,
  },
  graphApiMyPresence: {
    endpoint: 'https://graph.microsoft.com/v1.0/me/presence',
    scopes: AuthScopes.presence,
  },
  graphApiUsers: {
    endpoint: 'https://graph.microsoft.com/v1.0/users',
    scopes: AuthScopes.users,
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */

export const AuthRequest = {
  loginPopup: { scopes: [...AuthScopes.oidc, ...AuthScopes.user, ...AuthScopes.users, ...AuthScopes.teams, ...AuthScopes.presence] },
  login: { scopes: [...AuthScopes.oidc, ...AuthScopes.user] },
  teams: { scopes: AuthScopes.teams },
};

export interface IAuthRequest {
  scopes: string[];
}

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */

export const msalAuthBaseUrl = 'https://login.microsoftonline.com';

export function MSALInstanceFactory(
  confService: EnvironmentConfigurationService,
  loggingService: LoggingService,
): IPublicClientApplication {
  const config = confService.configurationSettings as AssistantEnvironmentConfiguration;
  /**
   * Configuration object to be passed to MSAL instance on creation.
   * For a full list of MSAL.js configuration parameters, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
   */
  const msalConfig: assistantMsalConfig = {
    cache: {
      // MemoryStorage — tokens never touch disk (no localStorage, no LevelDB exposure in the WebView2
      // profile). Cross-restart silent SSO is restored via a persisted UPN loginHint — see
      // AuthService.tryLogInSilently / processLogin.
      cacheLocation: BrowserCacheLocation.MemoryStorage,
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback(logLevel: LogLevel, message: string) {
          if (logLevel < LogLevel.Info) {
            loggingService.Error(message);
          }
        },
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  };
  msalConfig.auth = {
    clientId: config.acsAdAppId,
    authority: `${msalAuthBaseUrl}/${config.azureAdTenantId}`,
    redirectUri: isDevMode() ? `http://localhost:4200` : `https://assistant.${config.mainLocation}/`,
    postLogoutRedirectUri: isDevMode() ? `http://localhost:4200` : `https://assistant.${config.mainLocation}/`,
    navigateToLoginRequestUrl: true,
  };
  return new PublicClientApplication(msalConfig as Configuration);
}
/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  protectedResourceMap
    .set(protectedResources.graphApiMe.endpoint, protectedResources.graphApiMe.scopes)
    .set(protectedResources.graphApiUsers.endpoint, protectedResources.graphApiUsers.scopes)
    .set(protectedResources.graphApiMyPresence.endpoint, protectedResources.graphApiMyPresence.scopes);

  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap,
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    authRequest: AuthRequest.login,
  };
}
